<template>
  <div class="card mb-4">
    <b-form @submit.prevent="submit" class="rates-filters px-4 pt-3">
      <div class="filter-field filter-location">
        <label>Pickup</label>
        <LocationPicker v-model="pickupLocation" />
      </div>
      <div class="filter-field">
        <label>Pickup radius</label>
        <b-select size="sm" v-model="pickupRadius" :options="radiusOptions" />
      </div>
      <div class="filter-field filter-location">
        <label>Delivery</label>
        <LocationPicker v-model="deliveryLocation" />
      </div>
      <div class="filter-field">
        <label>Delivery radius</label>
        <b-select size="sm" v-model="deliveryRadius" :options="radiusOptions" />
      </div>
      <div class="filter-field">
        <label>History match</label>
        <b-select size="sm" v-model="historyMatchMode" :options="historyMatchModeOptions" />
      </div>
      <div class="filter-field">
        <label>Period</label>
        <b-select size="sm" v-model="period" :options="intervalsOptions" />
      </div>
      <button type="submit" class="btn btn-primary btn-sm filter-submit">Search</button>
    </b-form>

    <div class="card-body">
      <LoadingIndicator v-if="loading" centered />

      <div v-if="!loading && stats" class="mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <span class="mr-2">Median prices:</span>
            <span v-for="(value, name) in stats" :key="name" class="mr-2">
              {{ name }} - <b>{{ value }}</b>
            </span>
          </div>
          <div v-if="carsWaiting">
            <b-form-checkbox id="checkbox-1" v-model="showCarsWaiting" name="checkbox-1"> Cars waiting: <b>{{ carsWaiting }}</b> </b-form-checkbox>
          </div>
        </div>
        <LineChart v-if="items.length && datasets.data && datasets.labels" :charts-data="datasets" :show-cars-waiting="showCarsWaiting" :cars-waiting="carsWaiting" class="py-4"></LineChart>
      </div>

      <!-- Table -->
      <b-table
        v-if="!loading"
        :items="items"
        :fields="fields"
        show-empty
        empty-text="No results found for this search."
        striped
        bordered
        responsive
        class="card-table"
      >
        <template #cell(originZip)="data"> {{ data.item.originCity }} {{ data.item.originState }}, {{ data.item.originZip }} </template>

        <template #cell(destinationZip)="data"> {{ data.item.destinationCity }} {{ data.item.destinationState }}, {{ data.item.destinationZip }} </template>

        <template #cell(vehicleType)="data">
          {{ data.item.vehicleType }}<br />
          {{ data.item.vehicleMake }} {{ data.item.vehicleModel }} {{ data.item.vehicleYear }}<br />
          <div v-if="data.item.hasInopVehicle" class="text-danger">inoperable</div>
          <div v-if="data.item.trailerType && data.item.trailerType != 'OPEN'" class="text-danger">{{ data.item.trailerType.toLowerCase() }}</div>
          <div v-if="data.item.vehicleCount > 1" class="text-danger">{{ data.item.vehicleCount }} vehicles</div>
        </template>

        <template #cell(createdDate)="data">
          {{ data.item.createdDate | formatDate }}
        </template>

        <template #cell(availableDate)="data">
          {{ data.item.availableDate | formatDate }}
        </template>

        <template #cell(lastSeenAt)="data">
          {{ data.item.lastSeenAt | formatDateTime }}
        </template>
      </b-table>
      <!-- / Table -->

      <hr class="my-4" />
      <h5>Price History</h5>
      <p class="text-muted">Previous signed orders on the same or a similar route.</p>
      <LoadingIndicator v-if="historyLoading" centered />
      <template v-else>
        <div v-if="historyStats.count" class="mb-3">
          <span class="mr-4">Matches: <b>{{ historyStats.count }}</b></span>
          <span class="mr-4">Median customer price: <b>{{ historyStats.customerPrice | formatMoney }}</b></span>
          <span class="mr-4">Median carrier pay: <b>{{ historyStats.carrierPay | formatMoney }}</b></span>
          <span>Median margin: <b>{{ historyStats.margin | formatMoney }}</b></span>
        </div>
        <b-table
          :items="historyItems"
          :fields="historyFields"
          show-empty
          empty-text="No comparable shipments found for this search."
          striped
          bordered
          responsive
          class="card-table"
        >
          <template #cell(id)="data">
            <b-link :to="{ name: 'OrderDetails', params: { id: data.item.id } }">#{{ data.item.id }}</b-link>
          </template>
          <template #cell(route)="data">
            {{ data.item.originCity }}, {{ data.item.originState }} {{ data.item.originZip }}<br />
            {{ data.item.destinationCity }}, {{ data.item.destinationState }} {{ data.item.destinationZip }}
          </template>
          <template #cell(vehicle)="data">
            {{ data.item.vehicle }}<br />
            <small class="text-muted">{{ data.item.transportType }}<span v-if="data.item.vehicleCount > 1"> · {{ data.item.vehicleCount }} vehicles</span></small>
          </template>
          <template #cell(date)="data">{{ data.item.date | formatDate }}</template>
          <template #cell(customerPrice)="data">{{ data.item.customerPrice | formatMoney }}</template>
          <template #cell(carrierPay)="data">{{ data.item.carrierPay | formatMoney }}</template>
          <template #cell(margin)="data">{{ data.item.margin | formatMoney }}</template>
          <template #cell(pricePerMile)="data">{{ data.item.pricePerMile === null ? '-' : `$${data.item.pricePerMile}` }}</template>
        </b-table>
      </template>
    </div>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import LineChart from '@/components/shared/LineChart';
import LocationPicker from '@/components/shared/LocationPicker';

export default {
  name: 'MarketRatesList',
  data() {
    return {
      loading: false,
      loading2: false,
      historyLoading: false,
      items: [],
      historyItems: [],
      historyStats: {},
      datasets: {},
      labels: [],
      fields: [
        //    { key: 'listingId', label: 'Listing', sortable: true },
        //    { key: 'shipperOrderId', label: 'Order', sortable: true },
        { key: 'originZip', label: 'Origin', sortable: true },
        { key: 'destinationZip', label: 'Destination', sortable: true },
        { key: 'distance', label: 'Distance', sortable: true },
        { key: 'price', label: 'Price', sortable: true },
        { key: 'vehicleType', label: 'Vehicle' },
        { key: 'createdDate', label: 'Posted', sortable: true },
        { key: 'availableDate', label: 'Available', sortable: true },
        { key: 'lastSeenAt', label: 'Last seen', sortable: true },
      ],
      historyFields: [
        { key: 'id', label: 'Order', sortable: true },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'route', label: 'Route' },
        { key: 'vehicle', label: 'Vehicle' },
        { key: 'customerPrice', label: 'Customer Price', sortable: true },
        { key: 'carrierPay', label: 'Carrier Pay', sortable: true },
        { key: 'margin', label: 'Margin', sortable: true },
        { key: 'pricePerMile', label: 'Carrier $/mi', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
      ],
      pickupLocation: this.defaultPickupZip || this.leadQuoteOrder?.pickupLocation?.zip || '',
      pickupRadius: 50,
      deliveryLocation: this.defaultDeliveryZip || this.leadQuoteOrder?.deliveryLocation?.zip || '',
      deliveryRadius: 50,
      period: 365,
      historyMatchMode: 'state',
      historyMatchModeOptions: [
        { text: 'State to state', value: 'state' },
        { text: 'Radius', value: 'radius' },
      ],
      radiusOptions: [
        { text: 'exact match', value: 0 },
        { text: '25 miles', value: 25 },
        { text: '50 miles', value: 50 },
        { text: '100 miles', value: 100 },
      ],
      intervalsOptions: [
        { text: '1 Month', value: 30 },
        { text: '3 Months', value: 90 },
        { text: '6 Months', value: 180 },
        { text: '12 Months', value: 365 },
      ],
      stats: {},
      carsWaiting: null,
      showCarsWaiting: true,
    };
  },
  components: {
    LoadingIndicator,
    LineChart,
    LocationPicker,
  },
  props: {
    leadQuoteOrder: {
      type: Object,
      required: false,
      default: null,
    },
    defaultPickupZip: {
      type: String,
      required: false,
      default: null,
    },
    defaultDeliveryZip: {
      type: String,
      required: false,
      default: null,
    },
  },
  async created() {
    this.fetch();
    this.fetchMedianPrices();
    this.fetchPriceHistory();
  },
  methods: {
    async submit(e) {
      e.preventDefault();      
      this.fetchMedianPrices();
      this.fetch();
      this.fetchPriceHistory();
    },
    async fetch() {
      if (!this.pickupLocation || !this.deliveryLocation) return;
      this.loading = true;
      const response = await ApiClient.marketRates.get({
        params: { pickupRadius: this.pickupRadius, deliveryRadius: this.deliveryRadius, pickupLocation: this.pickupLocation, deliveryLocation: this.deliveryLocation },
      });
      this.items = response.data.data;
      this.stats = response.data.stats;
      this.carsWaiting = response.data.carsWaiting;
      this.loading = false;
    },
    async fetchMedianPrices() {
      if (!this.pickupLocation || !this.deliveryLocation) return;
      this.loading2 = true;
      const response = await ApiClient.marketRates.medianPrices({
        params: { pickupRadius: this.pickupRadius, deliveryRadius: this.deliveryRadius, pickupLocation: this.pickupLocation, deliveryLocation: this.deliveryLocation, period: Math.min(this.period, 30) },
      });
      this.datasets = response.data;
      this.labels = response.data.labels;
      this.loading2 = false;
    },
    async fetchPriceHistory() {
      if (!this.pickupLocation || !this.deliveryLocation) return;
      this.historyLoading = true;
      try {
        const response = await ApiClient.marketRates.priceHistory({
          params: {
            pickupRadius: this.pickupRadius,
            deliveryRadius: this.deliveryRadius,
            pickupLocation: this.pickupLocation,
            deliveryLocation: this.deliveryLocation,
            period: this.period,
            excludeLeadId: this.leadQuoteOrder?.id,
            matchMode: this.historyMatchMode,
            routeDistance: this.leadQuoteOrder?.distance,
          },
        });
        this.historyItems = response?.data?.data || [];
        this.historyStats = response?.data?.stats || {};
      } finally {
        this.historyLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.zip {
  width: 60px;
}

.rates-filters {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) 115px minmax(150px, 1fr) 115px 130px 110px auto;
  gap: 0.75rem;
  align-items: end;
}

.filter-field {
  min-width: 0;
}

.filter-field label {
  display: block;
  margin-bottom: 0.3rem;
  color: var(--secondary);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.filter-field ::v-deep .form-control,
.filter-field .custom-select {
  width: 100%;
}

.filter-submit {
  min-width: 76px;
}

@media (max-width: 1199.98px) {
  .rates-filters {
    grid-template-columns: minmax(170px, 1fr) 120px minmax(170px, 1fr) 120px;
  }

  .filter-submit {
    width: 100%;
  }
}

@media (max-width: 767.98px) {
  .rates-filters {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 479.98px) {
  .rates-filters {
    grid-template-columns: 1fr;
  }
}
</style>
